import { useEffect, useRef, useState } from 'react';
import { Plus, X, TerminalSquare, Clock3, Keyboard } from 'lucide-react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { TerminalSocket } from '../../services/websocket';
import { useStore } from '../../store/useStore';

interface TerminalTab {
  id: string;
  title: string;
  term: Terminal;
  fit: FitAddon;
  socket: TerminalSocket;
}

export function TerminalApp({ projectId: projectIdProp }: { projectName?: string; projectId?: string } = {}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [tabs, setTabs] = useState<TerminalTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const tabsRef = useRef<TerminalTab[]>([]);
  const activeTabRef = useRef<string | null>(null);
  const currentProject = useStore((s) => s.currentProject);
  const settings = useStore((s) => s.settings);
  const [statusByTab, setStatusByTab] = useState<Record<string, 'connecting' | 'connected' | 'disconnected'>>({});

  tabsRef.current = tabs;
  activeTabRef.current = activeTabId;

  // Focus the active tab's xterm instance. This is what makes physical
  // typing work after a click, and summons the virtual keyboard on touch
  // devices (xterm listens through a hidden textarea).
  const focusActiveTerm = () => {
    const active = tabsRef.current.find((t) => t.id === activeTabRef.current);
    if (active) {
      try {
        active.term.focus();
      } catch {}
    }
  };

  // Fit the active tab to its container and tell the pty its size.
  // Sizes are clamped: a 0-size container (hidden tab) must never
  // shrink the pty to 0x0 — that hangs fullscreen TUIs like opencode.
  const fitActiveTab = () => {
    const active = tabsRef.current.find((t) => t.id === activeTabRef.current);
    if (!active) return;
    try {
      active.fit.fit();
      const cols = Math.max(20, active.term.cols || 80);
      const rows = Math.max(10, active.term.rows || 24);
      active.socket.sendResize(cols, rows);
    } catch {}
  };

  // Init first terminal
  useEffect(() => {
    if (tabs.length === 0) {
      createTerminal();
    }
    // Re-fit whenever the container size changes (window resize/maximize).
    let roTimer: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver(() => {
      if (roTimer) clearTimeout(roTimer);
      roTimer = setTimeout(() => fitActiveTab(), 150);
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      if (roTimer) clearTimeout(roTimer);
      tabsRef.current.forEach((t) => {
        t.socket.disconnect();
        t.term.dispose();
      });
    };
  }, []);

  const createTerminal = (cwd?: string) => {
    if (!containerRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      fontSize: settings.terminalFontSize,
      fontFamily: settings.terminalFont || 'Menlo, Monaco, monospace',
      theme: {
        background: '#1a1b26',
        foreground: '#c0caf5',
        cursor: '#c0caf5',
        selectionBackground: '#33467c',
        black: '#1a1b26',
        red: '#f7768e',
        green: '#9ece6a',
        yellow: '#e0af68',
        blue: '#7aa2f7',
        magenta: '#bb9af7',
        cyan: '#7dcfff',
        white: '#c0caf5',
        brightBlack: '#414868',
        brightRed: '#f7768e',
        brightGreen: '#9ece6a',
        brightYellow: '#e0af68',
        brightBlue: '#7aa2f7',
        brightMagenta: '#bb9af7',
        brightCyan: '#7dcfff',
        brightWhite: '#a9b1d6',
      },
      scrollback: 10000,
      allowProposedApi: true,
      // Snappier feel: smoother scrolling and instant cursor response.
      fastScrollSensitivity: 7,
      scrollSensitivity: 1,
      cursorWidth: 2,
    });

    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());

    // Create div for this terminal
    const div = document.createElement('div');
    div.style.cssText = 'width:100%;height:100%;position:absolute;inset:0;';
    containerRef.current.appendChild(div);
    term.open(div);

    const tabId = `term-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const socket = new TerminalSocket(
      (data) => {
        // Filter control JSON messages, write raw PTY output
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'connected') {
            return;
          }
          if (parsed.type === 'exit') {
            term.write(`\r\n\x1b[36m[process exited with code ${parsed.code}]\x1b[0m\r\n`);
            return;
          }
        } catch {
          // Raw output
        }
        term.write(data);
      },
      (status) => {
        setStatusByTab((prev) => ({ ...prev, [tabId]: status }));
      }
    );

    const id = tabId;

    // Tag the container div so active tab management works
    div.setAttribute('data-term-id', id);
    div.style.display = 'block';

    // Resolve the real workspace dir. Project dirs on the server are named by
    // UUID, so a client-side `/workspace/<projectName>` guess never exists.
    // The server accepts the project id and maps it to the real path.
    const projectId = projectIdProp || currentProject?.id || null;
    const workspace = cwd || (projectId ? `/workspace/${currentProject?.name || projectId}` : '');
    socket.setTargetWorkspace(projectId, workspace || null);
    socket.connect();

    const newTab: TerminalTab = { id, title: currentProject?.name || 'Terminal', term, fit, socket };

    // Bind terminal input to socket
    term.onData((data) => socket.send(data));

    // Handle resize
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        fitActiveTab();
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    setTimeout(() => {
      try {
        fit.fit();
        const cols = Math.max(20, term.cols || 80);
        const rows = Math.max(10, term.rows || 24);
        socket.sendResize(cols, rows);
        term.focus();
      } catch {}
    }, 50);

    // Store resize cleanup
    (term as any)._resizeCleanup = () => window.removeEventListener('resize', handleResize);

    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(id);

    // Cleanup when terminal disposed
    const originalDispose = term.dispose.bind(term);
    (term as any).dispose = () => {
      if ((term as any)._resizeCleanup) (term as any)._resizeCleanup();
      socket.disconnect();
      setTimeout(() => div.remove(), 100);
      originalDispose();
    };
  };

  const closeTerminal = (id: string) => {
    const tab = tabsRef.current.find((t) => t.id === id);
    if (tab) {
      tab.socket.disconnect();
      tab.term.dispose();
    }
    setStatusByTab((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    const remaining = tabsRef.current
      .filter((t) => t.id !== id)
      .map((t) => ({ ...t, id: t.id }));
    if (remaining.length === 0) {
      setTabs([]);
      setActiveTabId(null);
      // Create a fresh one after a tick
      setTimeout(() => createTerminal(), 50);
      return;
    }
    setTabs(remaining.map((t) => ({ ...t })));
    if (activeTabRef.current === id) {
      const newActive = remaining[remaining.length - 1];
      setActiveTabId(newActive.id);
    }
  };

  // Render active terminal into its own div
  useEffect(() => {
    if (!containerRef.current) return;
    const active = activeTabId;
    const children = containerRef.current.querySelectorAll('[data-term-id]');
    children.forEach((el) => {
      (el as HTMLElement).style.display = el.getAttribute('data-term-id') === active ? 'block' : 'none';
    });
    if (active) {
      const t = setTimeout(() => { fitActiveTab(); focusActiveTerm(); }, 60);
      return () => clearTimeout(t);
    }
  }, [activeTabId, tabs]);

  return (
    <div className="h-full flex flex-col bg-[#1a1b26]">
      {/* Tab bar */}
      <div className="flex items-center bg-[#1f2335] border-b border-[#414868] px-1 py-0.5 gap-0.5 shrink-0">
        <div className="flex items-center gap-0.5 flex-1 overflow-x-auto">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              onClick={() => { setActiveTabId(tab.id); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-t-md text-xs cursor-pointer whitespace-nowrap transition-colors ${
                activeTabId === tab.id
                  ? 'bg-[#1a1b26] text-[#c0caf5]'
                  : 'text-[#565f89] hover:text-[#c0caf5] hover:bg-[#24283b]'
              }`}
            >
              <TerminalSquare className="w-3 h-3" />
              <span className="max-w-36 truncate">{tab.title}</span>
              <button
                onClick={(e) => { e.stopPropagation(); closeTerminal(tab.id); }}
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => createTerminal()}
          className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-[#24283b] text-[#565f89] hover:text-[#c0caf5] transition-colors shrink-0"
          title="New terminal"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => focusActiveTerm()}
          className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-[#24283b] text-[#565f89] hover:text-[#c0caf5] transition-colors shrink-0"
          title="Focus terminal / show keyboard"
        >
          <Keyboard className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Terminal container */}
      <div
        ref={containerRef}
        className="flex-1 relative"
        onMouseDown={() => focusActiveTerm()}
        onTouchStart={() => focusActiveTerm()}
      >
        {activeTabId && statusByTab[activeTabId] !== 'connected' && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#24283b] border border-[#414868] shadow-xl text-xs text-[#c0caf5]">
            {statusByTab[activeTabId] === 'disconnected' ? (
              <>
                <span className="w-2 h-2 rounded-full bg-red-400" />
                <span>Terminal disconnected</span>
                <button
                  onClick={() => {
                    tabsRef.current.find((t) => t.id === activeTabRef.current)?.socket.reconnect();
                  }}
                  className="px-2 py-0.5 rounded bg-[#7aa2f7] text-white hover:opacity-90"
                >
                  Retry
                </button>
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <span>Connecting to terminal…</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}